/**
 * Unified toast notification system
 * Based on Designer's toast (more complete) with EventBus integration
 */

import { eventBus, EVENTS } from '../core/event-bus.js'

const TOAST_DURATION = 3000
let container = null

function ensureContainer() {
  if (container?.isConnected) return container
  container = document.createElement('div')
  container.id = 'ws-toast-container'
  container.style.cssText = `
    position: fixed;
    top: 16px;
    right: 16px;
    z-index: 500;
    display: flex;
    flex-direction: column;
    gap: 8px;
    pointer-events: none;
  `
  document.body.appendChild(container)
  return container
}

/**
 * @typedef {object} ToastAction
 * @property {string} url   target URL — set via Element.href setter, never innerHTML
 * @property {string} label visible link text
 */

/**
 * @param {string} message               Plain-text body (rendered via textContent)
 * @param {'info'|'success'|'error'|'warning'} [type]
 * @param {number} [duration]            ms before auto-dismiss
 * @param {ToastAction} [action]         Optional opt-in action link
 */
export function showToast(message, type = 'info', duration = TOAST_DURATION, action = null) {
  const el = document.createElement('div')
  const colors = {
    success: 'var(--state-success)',
    error: 'var(--state-danger)',
    warning: 'var(--state-warning)',
    info: 'var(--state-info)',
  }
  const borderColor = colors[type] || colors.info

  el.style.cssText = `
    background: var(--surface-s3);
    border: 1px solid ${borderColor};
    border-left: 3px solid ${borderColor};
    border-radius: var(--radius-sm);
    padding: 10px 16px;
    color: var(--text-primary);
    font-size: 13px;
    font-family: var(--font-body);
    pointer-events: auto;
    opacity: 0;
    transform: translateX(20px);
    transition: all 250ms ease;
    max-width: 360px;
    box-shadow: var(--elev-2);
  `
  // textContent is the safe default. Past callers were passing concatenated
  // HTML strings (anchor tags with interpolated URLs) and the user just saw
  // literal `<a href=...>` text. Use the optional `action` slot below for
  // links — it builds the anchor with DOM APIs so a hostile URL can't
  // smuggle markup through the toast.
  el.textContent = message

  if (action && action.url && action.label) {
    el.appendChild(document.createTextNode(' '))
    const link = document.createElement('a')
    link.href = action.url
    link.target = '_blank'
    link.rel = 'noopener noreferrer'
    link.textContent = action.label
    link.style.cssText = 'color:var(--brand-primary);text-decoration:underline;'
    el.appendChild(link)
  }

  ensureContainer().appendChild(el)
  requestAnimationFrame(() => {
    el.style.opacity = '1'
    el.style.transform = 'translateX(0)'
  })

  setTimeout(() => {
    el.style.opacity = '0'
    el.style.transform = 'translateX(20px)'
    setTimeout(() => el.remove(), 300)
  }, duration)
}

// Listen for toast events
eventBus.on(EVENTS.TOAST_SHOW, ({ message, type, duration }) => {
  showToast(message, type, duration)
})

export default { showToast }
